import { router } from '@inertiajs/react'
import type { AgendaTemplateSection } from '../../types'

interface Props {
  section: AgendaTemplateSection
  index: number
  canEdit: boolean
  templateRequiredKind?: string | null
  onTitleChange: (id: number, title: string) => void
  onDurationChange: (id: number, minutes: number) => void
  onToggle: (id: number) => void
  dragHandleProps?: any
}

function requiredSectionTypesFor(kind?: string | null): string[] {
  if (kind === 'weekly_tactical') return ['set_the_stage', 'check_out']
  return ['check_out']
}

export default function AgendaSectionRow({ section, index, canEdit, templateRequiredKind, onTitleChange, onDurationChange, onToggle, dragHandleProps }: Props) {
  const isRequired = requiredSectionTypesFor(templateRequiredKind).includes(section.section_type)

  if (section.is_heading) {
    return (
      <div className="px-5 py-3 border-b border-stone last:border-0 bg-stone/10">
        {canEdit ? (
          <input
            type="text"
            value={section.title}
            onChange={e => onTitleChange(section.id, e.target.value)}
            className="w-full text-xs font-bold uppercase tracking-wider text-text-muted bg-transparent border-b border-transparent hover:border-stone focus:border-charcoal focus:outline-none px-0.5 py-1"
            aria-label="Heading text"
          />
        ) : (
          <span className="text-xs font-bold uppercase tracking-wider text-text-muted">{section.title}</span>
        )}
      </div>
    )
  }

  const handleDelete = () => {
    const message = section.is_system_section
      ? `Remove "${section.title}" from this template? You can add it back later from the Add Section menu.`
      : 'Delete this custom section?'
    if (!confirm(message)) return
    router.delete(`/agenda_templates/${section.agenda_template_id}/agenda_template_sections/${section.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <div className={[
      'flex items-start gap-4 px-5 py-4 border-b border-stone last:border-0 transition-colors',
      !section.is_enabled ? 'opacity-40' : 'hover:bg-[#F8F9FA]'
    ].join(' ')}>

      {canEdit && (
        <span {...dragHandleProps} className="text-stone cursor-grab active:cursor-grabbing flex-shrink-0 mt-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="5" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
        </span>
      )}

      <span className="w-7 h-7 rounded-full bg-stone flex items-center justify-center text-sm font-semibold text-text-primary flex-shrink-0 mt-1">
        {index + 1}
      </span>

      <div className="flex-1 min-w-0">
        {canEdit ? (
          <input
            type="text"
            value={section.title}
            onChange={e => onTitleChange(section.id, e.target.value)}
            disabled={!section.is_enabled}
            className="w-full text-sm font-medium text-text-primary bg-transparent border-b border-transparent hover:border-stone focus:border-charcoal focus:outline-none px-0.5 py-1 disabled:bg-transparent"
            aria-label="Section display name"
          />
        ) : (
          <span className="text-sm font-medium text-text-primary">{section.title}</span>
        )}
        {!section.is_system_section && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs font-bold uppercase tracking-wide text-text-primary bg-charcoal/5 px-2 py-0.5 rounded-full">
              Custom
            </span>
          </div>
        )}
      </div>

      {canEdit ? (
        <div className="flex items-center gap-1 mt-1">
          <input
            type="number" min={1} max={120}
            value={section.duration_minutes}
            disabled={!section.is_enabled}
            onChange={e => onDurationChange(section.id, parseInt(e.target.value) || 1)}
            className="w-14 text-center text-sm font-medium px-2 py-1.5 border border-stone rounded focus:border-charcoal focus:outline-none disabled:bg-stone"
          />
          <span className="text-sm text-text-muted">m</span>
        </div>
      ) : (
        <span className="text-sm text-text-muted w-14 text-center mt-1">{section.duration_minutes}m</span>
      )}

      {canEdit && section.is_system_section && !isRequired && (
        <button
          onClick={() => onToggle(section.id)}
          className={['w-10 h-5 rounded-full transition-colors flex-shrink-0 relative mt-2', section.is_enabled ? 'bg-pinnacle-red' : 'bg-stone'].join(' ')}
          aria-label={section.is_enabled ? 'Disable' : 'Enable'}
        >
          <span className={[
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
            section.is_enabled ? 'left-[22px]' : 'left-0.5'
          ].join(' ')} />
        </button>
      )}

      {canEdit && isRequired && (
        <>
          <div className="w-10 flex-shrink-0" />
          <div className="w-[14px] flex-shrink-0" />
        </>
      )}

      {canEdit && !isRequired && (
        <button onClick={handleDelete} className="opacity-30 hover:opacity-70 hover:text-pinnacle-red transition-colors flex-shrink-0 mt-2" aria-label="Remove section">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      )}
    </div>
  )
}
